/**
 * Página de Gerenciamento de Usuários (Admin)
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import {
  CheckCircleOutlined,
  CancelOutlined,
  WhatsApp,
  RefreshOutlined,
  PeopleOutlined,
  HourglassEmptyOutlined,
  TrendingUpOutlined
} from '@mui/icons-material';
import MainLayout from '../components/Layout/MainLayout';

interface UserData {
  id: number;
  uid: string;
  email: string;
  displayName: string;
  photoURL: string;
  whatsapp?: string;
  isApproved: boolean;
  isAdmin: boolean;
  createdAt: string;
  lastLoginAt: string;
  approvedAt?: string;
  rejectedAt?: string;
  rejectionReason?: string;
}

interface UserPerformance {
  uid: string;
  displayName: string;
  email: string;
  photoURL: string;
  isApproved: boolean;
  isAdmin: boolean;
  createdAt: string;
  totalGroups: number;
  totalLeads: number;
  leadsFechados: number;
  leadsContactados: number;
  leadsEmContato: number;
  leadsRecusados: number;
  taxaConversao: string;
  ultimaAtividade: string | null;
}

const UsersManagementPage: React.FC = () => {
  const [users, setUsers] = useState<UserData[]>([]);
  const [usersPerformance, setUsersPerformance] = useState<UserPerformance[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    loadUsers();
    loadUsersPerformance();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/auth/users`
      );
      const data = await response.json();
      setUsers(data);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUsersPerformance = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/auth/users/performance`
      );
      const data = await response.json();
      setUsersPerformance(data);
    } catch (error) {
      console.error('Erro ao carregar performance dos usuários:', error);
    }
  };

  const handleApprove = async (uid: string) => {
    try {
      // UID do admin logado deveria vir do contexto, mas por simplificação usarei o primeiro admin
      const adminUser = users.find(u => u.isAdmin);
      
      await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/auth/approve/${uid}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ approvedBy: adminUser?.uid || 'admin' })
        }
      );
      
      loadUsers();
      loadUsersPerformance();
    } catch (error) {
      console.error('Erro ao aprovar usuário:', error);
    }
  };

  const handleRejectClick = (user: UserData) => {
    setSelectedUser(user);
    setRejectDialogOpen(true);
    setRejectionReason('');
  };

  const handleRejectConfirm = async () => {
    if (!selectedUser || !rejectionReason.trim()) return;

    try {
      await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/auth/reject/${selectedUser.uid}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ rejectionReason })
        }
      );
      
      setRejectDialogOpen(false);
      setSelectedUser(null);
      loadUsers();
      loadUsersPerformance();
    } catch (error) {
      console.error('Erro ao rejeitar usuário:', error);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filtrar usuários por aba
  const pendingUsers = users.filter(u => !u.isApproved && !u.rejectedAt && !u.isAdmin);
  const approvedUsers = users.filter(u => u.isApproved || u.isAdmin);
  const rejectedUsers = users.filter(u => u.rejectedAt);

  const displayedUsers = tabValue === 1 ? pendingUsers : tabValue === 2 ? approvedUsers : tabValue === 3 ? rejectedUsers : [];

  if (loading) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#FF6B35' }} />
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Box>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" sx={{ color: '#FFFFFF', fontWeight: 700, mb: 1 }}>
              Gerenciamento de Usuários
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
              Aprove ou rejeite novos usuários do sistema
            </Typography>
          </Box>
          
          <Button
            variant="outlined"
            startIcon={<RefreshOutlined />}
            onClick={() => {
              loadUsers();
              loadUsersPerformance();
            }}
            sx={{
              color: '#FF6B35',
              borderColor: '#FF6B35',
              '&:hover': {
                borderColor: '#FF8A5B',
                backgroundColor: 'rgba(255, 107, 53, 0.1)',
              }
            }}
          >
            Atualizar
          </Button>
        </Box>

        {/* Tabs */}
        <Paper
          sx={{
            mb: 3,
            backgroundColor: '#1a1a1a',
            border: '1px solid rgba(255, 107, 53, 0.1)',
          }}
        >
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            sx={{
              '& .MuiTab-root': {
                color: 'rgba(255, 255, 255, 0.6)',
                '&.Mui-selected': {
                  color: '#FF6B35',
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#FF6B35',
              },
            }}
          >
            <Tab 
              label={`Performance (${usersPerformance.length})`} 
              icon={<TrendingUpOutlined />} 
              iconPosition="start"
            />
            <Tab 
              label={`Pendentes (${pendingUsers.length})`} 
              icon={<HourglassEmptyOutlined />} 
              iconPosition="start"
            />
            <Tab 
              label={`Aprovados (${approvedUsers.length})`} 
              icon={<CheckCircleOutlined />} 
              iconPosition="start"
            />
            <Tab 
              label={`Rejeitados (${rejectedUsers.length})`} 
              icon={<CancelOutlined />} 
              iconPosition="start"
            />
          </Tabs>
        </Paper>

        {/* Tabela de Performance */}
        {tabValue === 0 && (
          <TableContainer
            component={Paper}
            sx={{
              backgroundColor: '#1a1a1a',
              border: '1px solid rgba(255, 107, 53, 0.1)',
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#FF6B35', color: 'white', fontWeight: 'bold' }}>Usuário</TableCell>
                  <TableCell sx={{ bgcolor: '#FF6B35', color: 'white', fontWeight: 'bold' }}>Grupos</TableCell>
                  <TableCell sx={{ bgcolor: '#FF6B35', color: 'white', fontWeight: 'bold' }}>Total Leads</TableCell>
                  <TableCell sx={{ bgcolor: '#FF6B35', color: 'white', fontWeight: 'bold' }}>Fechados</TableCell>
                  <TableCell sx={{ bgcolor: '#FF6B35', color: 'white', fontWeight: 'bold' }}>Em Contato</TableCell>
                  <TableCell sx={{ bgcolor: '#FF6B35', color: 'white', fontWeight: 'bold' }}>Contactados</TableCell>
                  <TableCell sx={{ bgcolor: '#FF6B35', color: 'white', fontWeight: 'bold' }}>Recusados</TableCell>
                  <TableCell sx={{ bgcolor: '#FF6B35', color: 'white', fontWeight: 'bold' }}>Taxa Conversão</TableCell>
                  <TableCell sx={{ bgcolor: '#FF6B35', color: 'white', fontWeight: 'bold' }}>Última Atividade</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {usersPerformance.map((userPerf) => (
                  <TableRow 
                    key={userPerf.uid}
                    hover
                    sx={{ 
                      '&:hover': { backgroundColor: 'rgba(255, 107, 53, 0.05)' },
                      '& td': { color: 'rgba(255, 255, 255, 0.9)', borderColor: 'rgba(255, 255, 255, 0.1)' }
                    }}
                  >
                    {/* Usuário */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar 
                          src={userPerf.photoURL} 
                          sx={{ width: 40, height: 40 }}
                        >
                          {userPerf.displayName?.charAt(0) || userPerf.email?.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {userPerf.displayName || 'Sem nome'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                            {userPerf.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Grupos */}
                    <TableCell>
                      <Chip
                        label={userPerf.totalGroups || 0}
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(156, 39, 176, 0.2)',
                          color: '#9c27b0',
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>

                    {/* Total Leads */}
                    <TableCell>
                      <Typography variant="h6" sx={{ color: '#FF6B35', fontWeight: 700 }}>
                        {userPerf.totalLeads || 0}
                      </Typography>
                    </TableCell>

                    {/* Fechados */}
                    <TableCell>
                      <Chip
                        label={userPerf.leadsFechados || 0}
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(76, 175, 80, 0.2)',
                          color: '#4caf50',
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>

                    {/* Em Contato */}
                    <TableCell>
                      <Chip
                        label={userPerf.leadsEmContato || 0}
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(156, 39, 176, 0.2)',
                          color: '#9c27b0',
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>

                    {/* Contactados */}
                    <TableCell>
                      <Chip
                        label={userPerf.leadsContactados || 0}
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(33, 150, 243, 0.2)',
                          color: '#2196f3',
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>

                    {/* Recusados */}
                    <TableCell>
                      <Chip
                        label={userPerf.leadsRecusados || 0}
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(244, 67, 54, 0.2)',
                          color: '#f44336',
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>

                    {/* Taxa de Conversão */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            color: parseFloat(userPerf.taxaConversao) > 10 ? '#4caf50' : parseFloat(userPerf.taxaConversao) > 5 ? '#ff9800' : '#f44336',
                            fontWeight: 700 
                          }}
                        >
                          {userPerf.taxaConversao}%
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Última Atividade */}
                    <TableCell>
                      <Typography variant="body2">
                        {userPerf.ultimaAtividade 
                          ? new Date(userPerf.ultimaAtividade).toLocaleDateString('pt-BR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            })
                          : 'Sem atividade'
                        }
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Tabela de Usuários (Aprovação/Rejeição) */}
        {tabValue > 0 && (
          <>
            {displayedUsers.length === 0 ? (
              <Paper
                sx={{
                  p: 8,
                  backgroundColor: '#1a1a1a',
                  border: '1px solid rgba(255, 107, 53, 0.1)',
                  textAlign: 'center',
                }}
              >
                <PeopleOutlined sx={{ fontSize: 80, color: 'rgba(255, 107, 53, 0.3)', mb: 2 }} />
                <Typography variant="h5" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
                  Nenhum usuário {tabValue === 1 ? 'pendente' : tabValue === 2 ? 'aprovado' : 'rejeitado'}
                </Typography>
              </Paper>
            ) : (
              <TableContainer
            component={Paper}
            sx={{
              backgroundColor: '#1a1a1a',
              border: '1px solid rgba(255, 107, 53, 0.1)',
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#FF6B35', color: 'white', fontWeight: 'bold' }}>Usuário</TableCell>
                  <TableCell sx={{ bgcolor: '#FF6B35', color: 'white', fontWeight: 'bold' }}>Email</TableCell>
                  <TableCell sx={{ bgcolor: '#FF6B35', color: 'white', fontWeight: 'bold' }}>WhatsApp</TableCell>
                  <TableCell sx={{ bgcolor: '#FF6B35', color: 'white', fontWeight: 'bold' }}>Cadastrado Em</TableCell>
                  <TableCell sx={{ bgcolor: '#FF6B35', color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                  {tabValue === 1 && (
                    <TableCell sx={{ bgcolor: '#FF6B35', color: 'white', fontWeight: 'bold', width: 200 }}>Ações</TableCell>
                  )}
                  {tabValue === 3 && (
                    <TableCell sx={{ bgcolor: '#FF6B35', color: 'white', fontWeight: 'bold' }}>Motivo</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedUsers.map((userData) => (
                  <TableRow 
                    key={userData.id}
                    hover
                    sx={{ 
                      '&:hover': { backgroundColor: 'rgba(255, 107, 53, 0.05)' },
                      '& td': { color: 'rgba(255, 255, 255, 0.9)', borderColor: 'rgba(255, 255, 255, 0.1)' }
                    }}
                  >
                    {/* Usuário */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar 
                          src={userData.photoURL} 
                          sx={{ width: 40, height: 40 }}
                        >
                          {userData.displayName?.charAt(0) || userData.email?.charAt(0)}
                        </Avatar>
                        <Typography variant="body2" fontWeight="medium">
                          {userData.displayName || 'Sem nome'}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Email */}
                    <TableCell>
                      <Typography variant="body2">
                        {userData.email}
                      </Typography>
                    </TableCell>

                    {/* WhatsApp */}
                    <TableCell>
                      {userData.whatsapp ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2">{userData.whatsapp}</Typography>
                          <Tooltip title="Abrir WhatsApp">
                            <IconButton
                              size="small"
                              onClick={() => window.open(`https://wa.me/${userData.whatsapp?.replace(/\D/g, '')}`, '_blank')}
                            >
                              <WhatsApp sx={{ fontSize: '1rem', color: '#25D366' }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.4)' }}>
                          Não informado
                        </Typography>
                      )}
                    </TableCell>

                    {/* Data de Cadastro */}
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(userData.createdAt)}
                      </Typography>
                    </TableCell>

                    {/* Status */}
                    <TableCell>
                      {userData.isAdmin ? (
                        <Chip
                          label="Admin"
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(156, 39, 176, 0.2)',
                            color: '#9c27b0',
                            fontWeight: 600,
                          }}
                        />
                      ) : userData.isApproved ? (
                        <Chip
                          label="Aprovado"
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(76, 175, 80, 0.2)',
                            color: '#4caf50',
                            fontWeight: 600,
                          }}
                        />
                      ) : userData.rejectedAt ? (
                        <Chip
                          label="Rejeitado"
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(244, 67, 54, 0.2)',
                            color: '#f44336',
                            fontWeight: 600,
                          }}
                        />
                      ) : (
                        <Chip
                          label="Pendente"
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(255, 152, 0, 0.2)',
                            color: '#ff9800',
                            fontWeight: 600,
                          }}
                        />
                      )}
                    </TableCell>

                    {/* Ações (apenas para pendentes) */}
                    {tabValue === 1 && (
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<CheckCircleOutlined />}
                            onClick={() => handleApprove(userData.uid)}
                            sx={{
                              backgroundColor: '#4caf50',
                              '&:hover': { backgroundColor: '#45a049' },
                            }}
                          >
                            Aprovar
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<CancelOutlined />}
                            onClick={() => handleRejectClick(userData)}
                            sx={{
                              color: '#f44336',
                              borderColor: '#f44336',
                              '&:hover': {
                                borderColor: '#d32f2f',
                                backgroundColor: 'rgba(244, 67, 54, 0.1)',
                              },
                            }}
                          >
                            Rejeitar
                          </Button>
                        </Box>
                      </TableCell>
                    )}

                    {/* Motivo da Rejeição */}
                    {tabValue === 3 && (
                      <TableCell>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                          {userData.rejectionReason || '-'}
                        </Typography>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
            )}
          </>
        )}

        {/* Dialog de Rejeição */}
        <Dialog
          open={rejectDialogOpen}
          onClose={() => setRejectDialogOpen(false)}
          PaperProps={{
            sx: {
              backgroundColor: '#1a1a1a',
              border: '1px solid rgba(255, 107, 53, 0.3)',
            }
          }}
        >
          <DialogTitle sx={{ color: '#FFFFFF' }}>
            Rejeitar Usuário
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 3 }}>
              Usuário: <strong>{selectedUser?.displayName || selectedUser?.email}</strong>
            </Typography>
            
            <TextField
              autoFocus
              multiline
              rows={4}
              label="Motivo da Rejeição"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              fullWidth
              placeholder="Ex: Dados insuficientes, empresa não verificada, etc."
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: '#FFFFFF',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                  '&:hover fieldset': { borderColor: 'rgba(255, 107, 53, 0.5)' },
                  '&.Mui-focused fieldset': { borderColor: '#FF6B35' },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
              }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button
              onClick={() => setRejectDialogOpen(false)}
              sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleRejectConfirm}
              disabled={!rejectionReason.trim()}
              sx={{
                backgroundColor: '#f44336',
                '&:hover': { backgroundColor: '#d32f2f' },
              }}
            >
              Rejeitar
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </MainLayout>
  );
};

export default UsersManagementPage;
